import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Ticket as TicketIcon, ArrowClockwise, CaretLeft, MagnifyingGlass, PaperPlaneRight, NotePencil, Storefront, DeviceMobile, XCircle, WarningCircle } from "@phosphor-icons/react";
import { Shell } from "@/components/Shell";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";

const ADMIN_EMAIL = "office@mw3d.at";
const CAT_LABELS = { nfc_tag: "NFC-Tag", buddy: "SafeBuddy", qr: "QR-Code", login: "Login", group: "Gruppe", activation: "Aktivierung", shop: "Shop", premium: "Premium", other: "Sonstiges" };
const STATUS_LABELS = { OPEN: "Offen", IN_PROGRESS: "In Bearbeitung", WAITING_FOR_USER: "Wartet auf Nutzer", RESOLVED: "Gelöst", CLOSED: "Geschlossen" };
const STATUS_COLORS = { OPEN: "#EF4444", IN_PROGRESS: "#F59E0B", WAITING_FOR_USER: "#0EA5E9", RESOLVED: "#22C55E", CLOSED: "#6B7280" };
const PRIO_LABELS = { LOW: "Niedrig", NORMAL: "Normal", HIGH: "Hoch", URGENT: "Dringend" };
const SOURCE_META = {
  main: { label: "Hauptapp", color: "#7C3AED", Icon: DeviceMobile },
  business: { label: "Business", color: "#DC2626", Icon: Storefront },
};
const srcOf = (t) => (t.source === "business" ? "business" : "main");

function fmt(iso) {
  try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso || ""; }
}

function SourceBadge({ src }) {
  const m = SOURCE_META[src];
  const Icon = m.Icon;
  return (
    <span data-testid={`source-badge-${src}`} className="inline-flex items-center gap-1 text-[10px] font-heading font-medium px-2 py-0.5 rounded-full text-white shrink-0" style={{ background: m.color }}>
      <Icon size={11} weight="bold" />{m.label}
    </span>
  );
}

function TicketDetail({ id, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get(`/admin/support/tickets/${id}`); setData(r.data); }
    catch { toast.error("Ticket konnte nicht geladen werden"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="font-body text-sm text-gray-500">…</p>;
  const t = data.ticket;
  const src = srcOf(t);

  const update = async (body) => {
    setBusy(true);
    try { await api.put(`/admin/support/tickets/${id}`, body); await load(); onChanged && onChanged(); toast.success("Aktualisiert"); }
    catch { toast.error("Fehler"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="ticket-detail">
      <button data-testid="ticket-back" onClick={onBack} className="font-body text-sm text-gray-500 flex items-center gap-1"><CaretLeft size={14} />Alle Tickets</button>

      <div className="sb-card p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="font-heading font-bold">{t.ticket_no}</span>
            <SourceBadge src={src} />
          </div>
          <span className="text-[10px] uppercase px-2 py-0.5 rounded-full text-white" style={{ background: STATUS_COLORS[t.status] }}>{STATUS_LABELS[t.status]}</span>
        </div>
        {t.subject && <p className="font-heading font-semibold text-gray-900 mt-2">{t.subject}</p>}
        <p className="font-body text-xs text-gray-500 mt-1">{t.user_email || t.user_name || "—"} · {CAT_LABELS[t.category] || t.category} · {fmt(t.created_at)}</p>
        <p className="font-body text-sm text-gray-900 mt-2 whitespace-pre-wrap">{t.description || "—"}</p>
      </div>

      {t.tech && Object.keys(t.tech).length > 0 && (
        <details className="sb-card p-3" data-testid="ticket-tech">
          <summary className="cursor-pointer font-heading text-sm">Technischer Kontext</summary>
          <div className="mt-2 space-y-0.5 font-body text-[11px] text-gray-600 break-words">
            {Object.entries(t.tech).map(([k, v]) => <p key={k}><strong>{k}:</strong> {String(v)}</p>)}
          </div>
        </details>
      )}

      <div className="grid grid-cols-2 gap-2">
        <label className="font-body text-xs text-gray-600">Status
          <select data-testid="detail-status-select" disabled={busy} value={t.status} onChange={(e) => update({ status: e.target.value })} className="sb-input mt-1">
            {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="font-body text-xs text-gray-600">Priorität
          <select data-testid="detail-priority-select" disabled={busy} value={t.priority} onChange={(e) => update({ priority: e.target.value })} className="sb-input mt-1">
            {Object.entries(PRIO_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>

      <div className="sb-card p-3 space-y-2">
        <p className="font-heading text-sm font-semibold flex items-center gap-1"><PaperPlaneRight size={16} />Antwort per E-Mail</p>
        {!t.user_email && <p className="font-body text-[11px] text-amber-600">Keine Kontakt-E-Mail hinterlegt – Antwort kann nicht gesendet werden.</p>}
        <textarea data-testid="detail-reply" className="sb-input" rows={3} placeholder="Antwort an den Nutzer (wird per E-Mail gesendet)" value={reply} onChange={(e) => setReply(e.target.value)} />
        <button data-testid="detail-reply-send" disabled={busy || !reply.trim() || !t.user_email} onClick={() => { update({ reply }); setReply(""); }} className="sb-btn w-full py-2 text-white text-sm font-medium disabled:opacity-50" style={{ background: "var(--app-primary)" }}>Antwort senden</button>
        {t.replies?.length > 0 && <div className="space-y-1 pt-1" data-testid="detail-replies">{t.replies.map((r, i) => <p key={i} className="font-body text-[11px] text-gray-500 border-l-2 border-gray-300 pl-2 whitespace-pre-wrap">{r.text} <span className="text-gray-400">— {r.by}, {fmt(r.at)}</span></p>)}</div>}
      </div>

      <div className="sb-card p-3 space-y-2">
        <p className="font-heading text-sm font-semibold flex items-center gap-1"><NotePencil size={16} />Interne Notiz</p>
        <textarea data-testid="detail-note" className="sb-input" rows={2} placeholder="Nur intern sichtbar" value={note} onChange={(e) => setNote(e.target.value)} />
        <button data-testid="detail-note-add" disabled={busy || !note.trim()} onClick={() => { update({ note }); setNote(""); }} className="sb-btn w-full py-2 bg-white text-sm font-medium disabled:opacity-50">Notiz hinzufügen</button>
        {t.admin_notes?.length > 0 && <div className="space-y-1 pt-1">{t.admin_notes.map((n, i) => <p key={i} className="font-body text-[11px] text-gray-600 bg-amber-50 rounded-lg px-2 py-1">{n.text} <span className="text-gray-400">— {n.by}, {fmt(n.at)}</span></p>)}</div>}
      </div>

      {t.status !== "CLOSED" && (
        <button data-testid="detail-close-ticket" disabled={busy} onClick={() => update({ status: "CLOSED" })} className="sb-btn w-full py-2.5 bg-white text-sm font-medium flex items-center justify-center gap-1.5 text-gray-700"><XCircle size={16} weight="bold" />Ticket schließen</button>
      )}
    </div>
  );
}

export default function Tickets() {
  const { user, authChecked } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState({ total: 0, counts: {}, tickets: [] });
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [logs, setLogs] = useState([]);
  const prevCount = useRef(null);

  const allowed = authChecked && user && (user.email || "").toLowerCase() === ADMIN_EMAIL;

  useEffect(() => {
    if (authChecked && (!user || (user.email || "").toLowerCase() !== ADMIN_EMAIL)) navigate("/", { replace: true });
  }, [authChecked, user, navigate]);

  const loadTickets = useCallback(async (silent) => {
    try {
      const r = await api.get("/admin/support/tickets");
      setData(r.data);
      setLastSync(new Date());
      if (silent && prevCount.current != null && r.data.total > prevCount.current) {
        toast.success(`${r.data.total - prevCount.current} neues Ticket eingegangen`);
      }
      prevCount.current = r.data.total;
    } catch { if (!silent) toast.error("Tickets konnten nicht geladen werden"); }
  }, []);

  const loadLogs = useCallback(async () => {
    try { const r = await api.get("/admin/support/forward-logs"); setLogs(r.data.logs || []); } catch { /* ignore */ }
  }, []);

  const clearLogs = async () => {
    try { await api.post("/admin/support/forward-logs/clear"); setLogs([]); toast.success("Protokoll geleert"); }
    catch { toast.error("Fehler"); }
  };

  useEffect(() => {
    if (!allowed) return;
    loadTickets(false);
    loadLogs();
    const iv = setInterval(() => { loadTickets(true); loadLogs(); }, 15000);
    return () => clearInterval(iv);
  }, [allowed, loadTickets, loadLogs]);

  if (!allowed) return null;

  const filtered = data.tickets.filter((t) => {
    if (statusFilter && t.status !== statusFilter) return false;
    if (sourceFilter && srcOf(t) !== sourceFilter) return false;
    const s = q.trim().toLowerCase();
    if (s && !((t.ticket_no || "").toLowerCase().includes(s) || (t.user_email || "").toLowerCase().includes(s) || (t.subject || "").toLowerCase().includes(s) || (t.description || "").toLowerCase().includes(s))) return false;
    return true;
  });
  const srcCount = (src) => data.tickets.filter((t) => srcOf(t) === src).length;

  return (
    <Shell back="/dashboard" wide>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="tickets-page">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <TicketIcon size={28} weight="duotone" style={{ color: "var(--app-strong)" }} />
            <div>
              <h1 className="text-2xl font-heading font-semibold text-gray-900">Ticket-Zentrale</h1>
              <p className="font-body text-sm text-gray-500">Alle Support-Tickets aus Hauptapp &amp; Business</p>
            </div>
          </div>
          <button data-testid="tickets-refresh" onClick={() => loadTickets(false)} className="sb-btn p-2.5 bg-white shrink-0" title="Aktualisieren"><ArrowClockwise size={20} weight="bold" /></button>
        </div>

        {openId ? (
          <TicketDetail id={openId} onBack={() => { setOpenId(null); loadTickets(false); }} onChanged={() => loadTickets(true)} />
        ) : (
          <>
            {logs.length > 0 && (
              <div className="sb-card p-3 border-2 border-red-300 bg-red-50" data-testid="forward-log-panel">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-heading text-sm font-semibold text-red-700 flex items-center gap-1"><WarningCircle size={16} weight="fill" />Weiterleitung an Business fehlgeschlagen ({logs.length})</p>
                  <button data-testid="forward-log-clear" onClick={clearLogs} className="sb-btn px-2.5 py-1 bg-white text-xs shrink-0">Protokoll leeren</button>
                </div>
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto" data-testid="forward-log-list">
                  {logs.map((l, i) => (
                    <p key={i} className="font-body text-[11px] text-red-800 break-words">{fmt(l.at)} · Ticket {l.central_id || "—"} · {l.error}</p>
                  ))}
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2" data-testid="source-filters">
              <button data-testid="source-filter-all" onClick={() => setSourceFilter("")} className={`px-2.5 py-1 rounded-full text-xs font-heading border-2 ${sourceFilter === "" ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-300"}`}>Alle Apps ({data.tickets.length})</button>
              {["main", "business"].map((src) => (
                <button key={src} data-testid={`source-filter-${src}`} onClick={() => setSourceFilter(sourceFilter === src ? "" : src)}
                  className={`px-2.5 py-1 rounded-full text-xs font-heading border-2 ${sourceFilter === src ? "text-white border-gray-900" : "bg-white text-gray-600 border-gray-300"}`}
                  style={sourceFilter === src ? { background: SOURCE_META[src].color, borderColor: SOURCE_META[src].color } : {}}>
                  {SOURCE_META[src].label} ({srcCount(src)})
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2" data-testid="status-filters">
              {["OPEN", "IN_PROGRESS", "WAITING_FOR_USER", "RESOLVED", "CLOSED"].map((s) => (
                <button key={s} data-testid={`status-filter-${s}`} onClick={() => setStatusFilter(statusFilter === s ? "" : s)}
                  className={`px-2.5 py-1 rounded-full text-xs font-heading border-2 ${statusFilter === s ? "text-white border-gray-900" : "bg-white text-gray-600 border-gray-300"}`}
                  style={statusFilter === s ? { background: STATUS_COLORS[s], borderColor: STATUS_COLORS[s] } : {}}>
                  {STATUS_LABELS[s]} ({data.counts[s] || 0})
                </button>
              ))}
            </div>

            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input data-testid="tickets-search" className="sb-input pl-9" placeholder="Suche (Nr., E-Mail, Betreff, Text)" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>

            {lastSync && <p className="font-body text-[11px] text-gray-400" data-testid="tickets-lastsync">Automatische Aktualisierung alle 15 Sek. · zuletzt {lastSync.toLocaleTimeString("de-DE")}</p>}

            {filtered.length === 0 && <p className="font-body text-sm text-gray-500" data-testid="tickets-empty">Keine Tickets gefunden.</p>}
            <div className="grid gap-2 sm:grid-cols-2" data-testid="tickets-list">
              {filtered.map((t) => {
                const src = srcOf(t);
                return (
                  <button key={t.id} data-testid={`ticket-row-${t.ticket_no}`} onClick={() => setOpenId(t.id)} className="sb-card p-3 w-full text-left active:scale-[0.99] transition-transform">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-heading text-sm font-bold shrink-0">{t.ticket_no}</span>
                        <SourceBadge src={src} />
                      </div>
                      <span className="text-[10px] uppercase px-2 py-0.5 rounded-full text-white shrink-0" style={{ background: STATUS_COLORS[t.status] }}>{STATUS_LABELS[t.status]}</span>
                    </div>
                    <p className="font-body text-xs text-gray-500 truncate mt-1">{t.user_email || t.user_name || "—"} · {CAT_LABELS[t.category] || t.category} · {PRIO_LABELS[t.priority] || t.priority}</p>
                    {t.subject && <p className="font-body text-sm text-gray-900 truncate mt-0.5 font-medium">{t.subject}</p>}
                    <p className="font-body text-xs text-gray-700 truncate mt-0.5">{t.description || "—"}</p>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </motion.div>
    </Shell>
  );
}
